import React, { useState, useEffect } from 'react';
import { 
  Shield, Scale, Users, AlertTriangle, 
  FileText, Lock, Mail, ChevronRight, ArrowUp, Zap, HeartHandshake
} from 'lucide-react';

const sections = [
  { id: 'objet', title: "1. Objet & Concept", icon: HeartHandshake },
  { id: 'modele', title: "2. Modèle Économique", icon: Zap },
  { id: 'acces', title: "3. Accès & Inscription", icon: Lock },
  { id: 'responsabilite-users', title: "4. Responsabilités", icon: Users },
  { id: 'responsabilite-plateforme', title: "5. Rôle de ServicePro", icon: Shield },
  { id: 'litiges', title: "6. Droit applicable", icon: Scale },
  { id: 'contact', title: "7. Contact", icon: Mail },
];

const CGU = () => {
  const [activeSection, setActiveSection] = useState('objet');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      sections.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(id);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* --- HEADER --- */}
      <div className="bg-slate-900 text-white pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#e0692d] rounded-full filter blur-[120px] opacity-20 pointer-events-none"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">Conditions Générales d'Utilisation</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Transparence et confiance : découvrez comment <strong>ServicePro.tn</strong> facilite la mise en relation entre talents et clients.
          </p>
          <div className="mt-6 inline-block bg-white/5 backdrop-blur-md border border-white/10 px-4 py-2 rounded-full">
            <p className="text-xs uppercase tracking-widest text-slate-300">
              Dernière mise à jour : <span className="text-[#e0692d] font-bold">Avril 2026</span>
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* --- SIDEBAR NAVIGATION --- */}
          <aside className="hidden lg:block w-1/4">
            <div className="sticky top-24 bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-6 pl-4">Sommaire</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center justify-between px-4 py-3.5 text-sm font-bold rounded-2xl transition-all duration-300 ${
                      activeSection === section.id
                        ? 'bg-orange-50 text-[#e0692d] shadow-sm shadow-orange-100'
                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <section.icon className={`w-5 h-5 ${activeSection === section.id ? 'text-[#e0692d]' : 'text-slate-300'}`} />
                      {section.title}
                    </span>
                    {activeSection === section.id && <ChevronRight className="w-4 h-4" />}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* --- CONTENU PRINCIPAL --- */}
          <main className="lg:w-3/4 space-y-12">
            
            {/* Introduction - Le Concept */}
            <div className="bg-gradient-to-br from-white to-slate-50 p-10 rounded-[2.5rem] shadow-sm border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5">
                    <HeartHandshake size={120} />
                </div>
              <p className="text-slate-600 leading-relaxed text-xl relative z-10">
                <strong>ServicePro.tn</strong> est une marketplace technologique de mise en relation. 
                Notre mission est de connecter les <strong>professionnels et artisans</strong> tunisiens 
                avec des <strong>clients</strong> (particuliers ou entreprises) à la recherche d'expertise. 
                Nous agissons comme un facilitateur numérique pour dynamiser le marché local.
              </p>
            </div>

            {/* 1. OBJET */}
            <section id="objet" className="scroll-mt-32">
              <h2 className="text-3xl font-black mb-6 flex items-center gap-4 text-slate-900">
                <span className="w-10 h-10 rounded-xl bg-orange-100 text-[#e0692d] flex items-center justify-center text-lg">1</span>
                Objet & Concept
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600 text-lg">
                <p>
                  Les présentes CGU définissent les règles d'utilisation de la plateforme ServicePro.tn. 
                  En tant que place de marché, nous permettons :
                </p>
                <ul className="grid md:grid-cols-2 gap-4 mt-6 list-none p-0">
                  <li className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-3">
                    <div className="bg-green-100 p-1 rounded-full"><ChevronRight size={14} className="text-green-600"/></div>
                    <span>Aux <strong>Professionnels</strong> de lister leurs services, galeries et tarifs.</span>
                  </li>
                  <li className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-start gap-3">
                    <div className="bg-green-100 p-1 rounded-full"><ChevronRight size={14} className="text-green-600"/></div>
                    <span>Aux <strong>Clients</strong> de trouver l'expert idéal gratuitement.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* 2. MODÈLE ÉCONOMIQUE - LA GRATUITÉ */}
            <section id="modele" className="scroll-mt-32 bg-orange-600 rounded-[2.5rem] p-10 text-white shadow-xl shadow-orange-200 relative overflow-hidden">
              <div className="absolute -bottom-10 -left-10 opacity-10 rotate-12">
                <Zap size={200} />
              </div>
              <h2 className="text-3xl font-black mb-6 flex items-center gap-4">
                <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-lg">2</span>
                Modèle Économique
              </h2>
              <div className="relative z-10 space-y-6 text-orange-50">
                <p className="text-xl font-medium leading-relaxed">
                  L'accès et l'utilisation de ServicePro.tn sont <strong>totalement gratuits</strong> pour tous les utilisateurs.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20">
                    <h4 className="font-bold text-white mb-2">Zéro Commission</h4>
                    <p className="text-sm opacity-90 text-white">Nous ne prenons aucune commission sur les transactions entre Clients et Professionnels. Le paiement de la prestation se fait directement entre vous.</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20">
                    <h4 className="font-bold text-white mb-2">Option Sponsoring</h4>
                    <p className="text-sm opacity-90 text-white">Seul le <strong>Sponsoring de service</strong> est payant. Cette option facultative permet aux professionnels d'augmenter leur visibilité en haut des résultats de recherche.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. ACCÈS AU SERVICE */}
            <section id="acces" className="scroll-mt-32">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-900">
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-900 flex items-center justify-center text-sm font-bold">3</span>
                Accès & Inscription
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600">
                <p>
                  Tout professionnel doit créer un compte vérifié pour proposer ses services. 
                  L'inscription nécessite des informations exactes (identifiants, métier, ville). 
                  La plateforme se réserve le droit de suspendre tout compte ne respectant pas les standards de qualité.
                </p>
              </div>
            </section>

            {/* 4. RESPONSABILITÉS */}
            <section id="responsabilite-users" className="scroll-mt-32">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900">
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-900 flex items-center justify-center text-sm font-bold">4</span>
                Responsabilités des Utilisateurs
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-3xl border border-slate-200">
                  <h3 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" /> Côté Client
                  </h3>
                  <ul className="text-sm text-slate-500 space-y-3 list-none p-0">
                    <li className="flex gap-2"><span>•</span> Expliciter clairement ses besoins pour un devis précis.</li>
                    <li className="flex gap-2"><span>•</span> Noter le professionnel de manière honnête et constructive.</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-slate-200">
                  <h3 className="font-black text-slate-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-orange-600" /> Côté Professionnel
                  </h3>
                  <ul className="text-sm text-slate-500 space-y-3 list-none p-0">
                    <li className="flex gap-2"><span>•</span> Être en règle avec la législation tunisienne (patente, assurances).</li>
                    <li className="flex gap-2"><span>•</span> Fournir des visuels réels et non trompeurs de ses réalisations.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 5. RÔLE DE LA PLATEFORME */}
            <section id="responsabilite-plateforme" className="scroll-mt-32">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-900">
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-900 flex items-center justify-center text-sm font-bold">5</span>
                Rôle de ServicePro
              </h2>
              <div className="bg-slate-100 rounded-3xl p-8 border border-slate-200">
                <div className="flex gap-6">
                  <AlertTriangle className="w-10 h-10 text-orange-500 flex-shrink-0" />
                  <div className="text-slate-600 text-base leading-relaxed">
                    <p className="mb-4"><strong>ServicePro n'est pas l'employeur des professionnels.</strong></p>
                    <p>
                      Nous ne sommes pas partie prenante aux contrats conclus entre les utilisateurs. 
                      Notre responsabilité est limitée à la fourniture technique de l'outil de recherche. 
                      En cas de litige technique sur le service rendu, la responsabilité incombe exclusivement au professionnel choisi.
                    </p>
                  </div>
                </div>
              </div>
            </section>

             {/* 6. LITIGES */}
             <section id="litiges" className="scroll-mt-32">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-slate-900">
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-900 flex items-center justify-center text-sm font-bold">6</span>
                Droit applicable & Litiges
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600">
                <p>
                  Les présentes CGU sont soumises au <strong>Droit Tunisien</strong>. En cas de désaccord, nous encourageons 
                  systématiquement une résolution amiable via notre support avant toute action judiciaire devant les tribunaux de Tunis.
                </p>
              </div>
            </section>

            {/* 7. CONTACT */}
            <section id="contact" className="bg-[#0f172a] text-white p-10 rounded-[3rem] relative overflow-hidden scroll-mt-32 shadow-2xl">
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-10">
                <div>
                  <h2 className="text-3xl font-black mb-3">Besoin d'aide ?</h2>
                  <p className="text-slate-400">Notre équipe support vous répond sous 24h pour toute question relative à nos services.</p>
                </div>
                <a 
                  href="mailto:contact@servicepro.tn" 
                  className="group bg-[#e0692d] hover:bg-white text-white hover:text-[#e0692d] px-8 py-4 rounded-2xl font-black transition-all shadow-xl flex items-center gap-3 whitespace-nowrap"
                >
                  <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  contact@servicepro.tn
                </a>
              </div>
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
            </section>

          </main>
        </div>
      </div>
      
      {/* Bouton retour haut mobile */}
      <button 
        onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
        className="lg:hidden fixed bottom-6 right-6 bg-orange-600 text-white p-4 rounded-2xl shadow-2xl z-50 hover:scale-110 transition-transform"
      >
        <ArrowUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default CGU;
